import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { api } from '../services/api';

const SearchableDropdown = ({
    apiEndpoint,
    initialValue = "",
    placeholder = "Select...",
    queryParams = {},
    labelKey = "Label",
    valueKey = "Label",
    disabled = false,
    returnFullObject = false,
    onChange
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const [menuPos, setMenuPos] = useState(null);
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const inputRef = useRef(null);
    const lastFetchedKeyRef = useRef(null);

    const paramsKey = JSON.stringify(queryParams);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                (!menuRef.current || !menuRef.current.contains(event.target))
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Initial value handling
    useEffect(() => {
        if (initialValue) {
            setSelectedLabel(initialValue);
        }
    }, [initialValue]);

    // Fetch options while open, but only when the params differ from the last fetch
    useEffect(() => {
        if (!isOpen || lastFetchedKeyRef.current === paramsKey) return;

        let cancelled = false;
        const fetchOptions = async () => {
            setLoading(true);
            setFetchError(false);
            try {
                const response = await api.get(apiEndpoint, { params: JSON.parse(paramsKey) });
                if (cancelled) return;
                setOptions(Array.isArray(response.data) ? response.data : []);
                lastFetchedKeyRef.current = paramsKey;
            } catch (error) {
                console.error("Error fetching options:", error);
                if (cancelled) return;
                setOptions([]);
                setFetchError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchOptions();
        return () => { cancelled = true; };
    }, [isOpen, paramsKey, apiEndpoint]);

    const updateMenuPosition = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const rect = trigger.getBoundingClientRect();
        const openUp = rect.top > window.innerHeight - rect.bottom;
        setMenuPos({
            left: rect.left,
            width: rect.width,
            top: openUp ? undefined : rect.bottom + 4,
            bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
        });
    }, []);

    // Keep the portaled menu anchored to the trigger; capture phase because the
    // planning table's inner scroller scrolls, not window
    useEffect(() => {
        if (!isOpen) return;
        updateMenuPosition();
        window.addEventListener('scroll', updateMenuPosition, true);
        window.addEventListener('resize', updateMenuPosition);
        return () => {
            window.removeEventListener('scroll', updateMenuPosition, true);
            window.removeEventListener('resize', updateMenuPosition);
        };
    }, [isOpen, updateMenuPosition]);

    const filteredOptions = options.filter(option =>
        String(option[labelKey] || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelect = (option) => {
        const value = returnFullObject ? option : option[valueKey];
        const label = option[labelKey];
        setSelectedLabel(label);
        if (onChange) {
            onChange(value);
        }
        setIsOpen(false);
        setSearchTerm(""); // Reset search on select
    };

    const handleTriggerClick = () => {
        if (disabled) return;
        if (isOpen) {
            setIsOpen(false);
        } else {
            setActiveIndex(0);
            updateMenuPosition();
            setIsOpen(true);
        }
    };

    const handleKeyDown = (e) => {
        if (!isOpen) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(prev => Math.min(prev + 1, Math.max(filteredOptions.length - 1, 0)));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const option = filteredOptions[activeIndex] || filteredOptions[0];
            if (option) handleSelect(option);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setIsOpen(false);
            setSearchTerm("");
            if (triggerRef.current) triggerRef.current.focus();
        }
    };

    const styles = {
        trigger: {
            height: '32px', // Compact size to match surrounding controls
            padding: '0 10px',
            fontSize: '0.78rem',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            textAlign: 'left',
            boxSizing: 'border-box',
        },
        disabled: {
            opacity: 0.5,
            cursor: 'not-allowed',
        },
        active: {
            borderColor: 'var(--accent)',
            background: 'var(--accent-softer)',
        },
        valueText: {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginRight: '5px',
            flex: 1,
        },
        arrow: {
            fontSize: '0.55rem',
            color: 'var(--accent)',
            marginLeft: '4px',
        },
        dropdownMenu: {
            position: 'fixed',
            zIndex: 1100,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            maxHeight: 'min(300px, 45vh)',
        },
        searchContainer: {
            padding: 'var(--space-2)',
            borderBottom: '1px solid var(--line-subtle)',
            flexShrink: 0,
        },
        searchInput: {
            height: '30px',
            fontSize: '0.8rem',
            boxSizing: 'border-box',
        },
        optionsList: {
            maxHeight: '200px',
            overflowY: 'auto',
            padding: '4px',
        },
        option: {
            padding: '7px 10px',
            fontSize: '0.8rem',
            color: 'var(--text-primary)',
            cursor: 'pointer',
            borderRadius: 'var(--radius-xs)',
            transition: 'background-color var(--dur-fast) ease',
        },
        statusMsg: {
            padding: '12px',
            fontSize: '0.8rem',
            color: 'var(--text-tertiary)',
            textAlign: 'center',
        }
    };

    return (
        <div ref={dropdownRef} style={{ position: 'relative', width: '100%' }} onKeyDown={handleKeyDown}>
            {/* Trigger Button */}
            <button
                ref={triggerRef}
                type="button"
                className="input"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                style={{
                    ...styles.trigger,
                    ...(isOpen ? styles.active : {}),
                    ...(disabled ? styles.disabled : {})
                }}
                onClick={handleTriggerClick}
            >
                <span style={styles.valueText}>
                    {disabled ? (placeholder || "Disabled") : (selectedLabel || placeholder)}
                </span>
                <span style={styles.arrow}>{!disabled && (isOpen ? '▲' : '▼')}</span>
            </button>

            {/* Dropdown Content (portaled to escape overflow clipping) */}
            {isOpen && menuPos && ReactDOM.createPortal(
                <div
                    ref={menuRef}
                    className="glass"
                    style={{
                        ...styles.dropdownMenu,
                        left: menuPos.left,
                        width: menuPos.width,
                        top: menuPos.top,
                        bottom: menuPos.bottom,
                    }}
                >
                    {/* Search Input */}
                    <div style={styles.searchContainer}>
                        <input
                            ref={inputRef}
                            type="text"
                            className="input"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setActiveIndex(0);
                            }}
                            style={styles.searchInput}
                            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking input
                            autoFocus
                        />
                    </div>

                    {/* Options List */}
                    <div style={styles.optionsList} role="listbox">
                        {loading ? (
                            <div style={styles.statusMsg}>Loading...</div>
                        ) : fetchError ? (
                            <div style={{ ...styles.statusMsg, color: 'var(--danger)' }}>Failed to load options</div>
                        ) : filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={index}
                                    role="option"
                                    aria-selected={option[labelKey] === selectedLabel}
                                    style={{
                                        ...styles.option,
                                        backgroundColor: index === activeIndex ? 'var(--accent-soft)' : 'transparent',
                                    }}
                                    onClick={() => handleSelect(option)}
                                    onMouseEnter={() => setActiveIndex(index)}
                                >
                                    {option[labelKey]}
                                </div>
                            ))
                        ) : (
                            <div style={styles.statusMsg}>No matches found</div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default SearchableDropdown;
